import React, { useEffect, useMemo } from "react";
import { Modal, Form, Input, InputNumber, Select, Button, Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  mealsActions,
  packsActions,
  packCategoryActions,
} from "../../../../store/slices/settings-slices";
import { packsService } from "../../../../services/settings-services";
import { getConfig } from "../../../../config";

const { TextArea } = Input;

const PacksCreatePackModal = ({ setIsOpen }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { cityCode } = getConfig();
  const { mealsArray, trigger } = useSelector((store) => store.meals);
  const { packCategories, trigger: packCategoryTrigger } = useSelector((store) => store.packCategories);

  const currentLanguage = i18n.language;
  const languages = useMemo(() => 
    cityCode === "UAE" ? ["en", "ru", "ar"] : ["en", "ru", "ge"],
    [cityCode]
  );

  useEffect(() => {
    dispatch(mealsActions.getAllMeals());
    dispatch(packCategoryActions.getAllPackCategories());
  }, [dispatch, trigger, packCategoryTrigger]);

  const handleSubmit = async (values) => {
    try {
      const formattedData = {
        name: languages.reduce((acc, lang) => {
          acc[lang] = values[`name_${lang}`];
          return acc;
        }, {}),
        description: languages.reduce((acc, lang) => {
          acc[lang] = values[`description_${lang}`];
          return acc;
        }, {}),
        pricePerDay: values.pricePerDay || 0,
        number: values.number || 0,
        energy: values.energy || 0,
        mealIds: values.mealIds || [],
        dietModeId: null,
        packCategoryId: values.packCategoryId,
      };

      await packsService.createPack(formattedData);
      dispatch(packsActions.changeTrigger());
      toast.success(t("Pack created successfully"));
      setIsOpen(false);
      form.resetFields();
    } catch (error) {
      console.error("Error creating pack:", error);
      toast.error(t("Failed to create pack"));
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    form.resetFields();
  };

  // Format meals for Select component
  const mealOptions = mealsArray?.map((meal) => ({
    label: meal.name?.[currentLanguage] || meal.name?.en || meal.name,
    value: meal.id,
  })) || [];

  // Format pack categories for Select component — active only
  const packCategoryOptions = packCategories?.filter((cat) => cat.isActive).map((cat) => ({
    label: cat.name?.[currentLanguage] || cat.name?.en || cat.name,
    value: cat.id,
  })) || [];

  const getLanguageLabel = (lang) => {
    const labels = {
      en: "English",
      ru: "Русский",
      ar: "العربية",
      ge: "ქართული",
    };
    return labels[lang] || lang.toUpperCase();
  };

  return (
    <Modal
      title={t("Create New Pack")}
      open={true}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        {/* Name Inputs for each language */}
        <Row gutter={16}>
          {languages.map((lang) => (
            <Col span={8} key={`name_${lang}`}>
              <Form.Item
                label={`${t("Name")} (${getLanguageLabel(lang)})`}
                name={`name_${lang}`}
                rules={[
                  {
                    required: true,
                    message: t(`Name in ${getLanguageLabel(lang)} is required`),
                  },
                ]}
              >
                <Input placeholder={t(`Enter pack name in ${getLanguageLabel(lang)}`)} />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Description Inputs for each language */}
        <Row gutter={16}>
          {languages.map((lang) => (
            <Col span={8} key={`description_${lang}`}>
              <Form.Item
                label={`${t("Description")} (${getLanguageLabel(lang)})`}
                name={`description_${lang}`}
              >
                <TextArea
                  rows={2}
                  placeholder={t(`Enter description in ${getLanguageLabel(lang)}`)}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Meals Multi-Select */}
        <Form.Item
          label={t("Select Meals")}
          name="mealIds"
          rules={[
            {
              required: false,
              message: t("Please select at least one meal"),
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder={t("Select meals for this pack")}
            options={mealOptions}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            showSearch
            allowClear
          />
        </Form.Item>

        {/* Pack Category Select */}
        <Form.Item
          label={t("Select Pack Category")}
          name="packCategoryId"
          rules={[{ required: true, message: t("Please select a pack category") }]}
        >
          <Select
            placeholder={t("Select category for this pack")}
            options={packCategoryOptions}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            showSearch
          />
        </Form.Item>

        {/* Number, Energy, and Price Inputs */}
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label={t("Number")}
              name="number"
              initialValue={0}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                placeholder={t("Enter number")}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t("Energy")}
              name="energy"
              initialValue={0}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                placeholder={t("Enter energy value")}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t("Price Per Day")}
              name="pricePerDay"
              initialValue={0}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                placeholder={t("Enter price per day")}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Form.Item style={{ marginBottom: 0, marginTop: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button onClick={handleCancel}>{t("Cancel")}</Button>
            <Button type="primary" htmlType="submit">
              {t("Create")}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export { PacksCreatePackModal };
